// Affiliate auth provider — fully isolated from consumer auth.
// Same browser can be signed into both at once via separate token keys.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { affGet, affPost, getAffiliateToken, setAffiliateToken, clearAffiliateToken } from "@/lib/affiliateApi";

const AffiliateAuthCtx = createContext(null);

export function AffiliateAuthProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  const refreshProfile = useCallback(async () => {
    if (!getAffiliateToken()) {
      setProfile(null);
      return null;
    }
    try {
      const me = await affGet("/affiliate/profile", { auth: true });
      setProfile(me);
      return me;
    } catch (err) {
      // 401 → token invalid; clear and force re-login.
      if (err.status === 401) clearAffiliateToken();
      setProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshProfile();
      setBootstrapped(true);
    })();
  }, [refreshProfile]);

  const login = useCallback(async (email, password) => {
    const data = await affPost("/affiliate/auth/login", { email, password });
    if (data?.token) setAffiliateToken(data.token);
    if (data?.profile) setProfile(data.profile);
    else if (data?.token) await refreshProfile();
    return data;
  }, [refreshProfile]);

  const logout = useCallback(() => {
    clearAffiliateToken();
    setProfile(null);
    if (typeof window !== "undefined") window.location.href = "/affiliates";
  }, []);

  const value = useMemo(
    () => ({ profile, bootstrapped, login, logout, refreshProfile, setProfile }),
    [profile, bootstrapped, login, logout, refreshProfile],
  );

  return <AffiliateAuthCtx.Provider value={value}>{children}</AffiliateAuthCtx.Provider>;
}

export function useAffiliateAuth() {
  const ctx = useContext(AffiliateAuthCtx);
  if (!ctx) throw new Error("useAffiliateAuth must be used inside AffiliateAuthProvider");
  return ctx;
}
